// Documentos emitidos en Random (pedido de Rodrigo, 2026-10-09): buscar una factura, boleta, guía,
// nota de crédito o nota de venta del sistema anterior por su folio y verla completa (e imprimirla).
// Es historia de SOLO LECTURA cargada desde el archivo histórico: no cuenta para saldos ni stock.
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { Modal } from '../comun/Modal';
import { decimal, fecha, pesos, rut } from '../comun/formato';

export const TIPOS_RANDOM: Record<string, string> = {
  FCV: 'Factura',
  BLV: 'Boleta',
  GDV: 'Guía de despacho',
  NCV: 'Nota de crédito',
  FDV: 'Factura de exportación',
  NVV: 'Nota de venta',
};

/** Empresas de Random: MADECI SPA es la 02; antes operó como 01 y 03. */
const EMPRESAS_RANDOM: Record<string, string> = {
  '02': '02 · MADECI SPA',
  '03': '03 · (2025–ago 2026)',
  '01': '01 · Comercial Madeci (2022–2025)',
};

export interface DocumentoRandomListado {
  id: string;
  empresa: string;
  tido: string;
  folio: string;
  rut: string | null;
  cliente: string | null;
  fecha: string;
  vencimiento: string | null;
  total: string;
  abonado: string;
  estado: string;
}

export interface DocumentoRandom extends DocumentoRandomListado {
  sucursalCliente: string | null;
  sucursal: string | null;
  funcionario: string | null;
  neto: string;
  iva: string;
  impuestos: string;
  electronico: boolean | null;
  observacion: string | null;
  ordenCompra: string | null;
  direccionDespacho: string | null;
  emisor: {
    empresa: string;
    razon: string;
    rut: string | null;
    giro: string | null;
    direccion: string | null;
    ciudad: string | null;
  };
  lineas: Array<{
    linea: number;
    sku: string | null;
    descripcion: string | null;
    cantidad: string | null;
    unidad: string | null;
    precioBruto: string | null;
    descuentoPct: string | null;
    descuento: string | null;
    neto: string | null;
    bruto: string | null;
    origenTido: string | null;
    origenFolio: string | null;
  }>;
}

/** El estado de Random en palabras: nulo, pagado (cerrado) o con saldo. */
export function estadoRandom(d: Pick<DocumentoRandomListado, 'estado' | 'total' | 'abonado'>) {
  if (d.estado === 'N') return 'Nulo';
  if (d.estado === 'C') return 'Cerrado';
  const saldo = Number(d.total) - Number(d.abonado);
  return saldo > 0 ? `Saldo ${pesos(String(Math.round(saldo)))}` : 'Abierto';
}

/** «0000002150» → «2150»; «C000000001» → «C1» (el backend ya lo entrega así; esto es para el origen). */
const folioCorto = (f: string | null) => (f ? f.replace(/^([A-Za-z]*)0*(\d+)$/, '$1$2') : '');

interface Filtros {
  empresa: string;
  tipo: string;
  folio: string;
  cliente: string;
  desde: string;
  hasta: string;
  pagina: number;
}

export function DocumentosRandom() {
  const [f, setF] = useState<Filtros>({
    empresa: '02',
    tipo: 'FCV',
    folio: '',
    cliente: '',
    desde: '',
    hasta: '',
    pagina: 1,
  });
  const [abierto, setAbierto] = useState<string | null>(null);
  const cambiar = (c: Partial<Filtros>) => setF((x) => ({ ...x, pagina: 1, ...c }));
  const consulta = {
    ...(f.empresa ? { empresa: f.empresa } : {}),
    ...(f.tipo ? { tipo: f.tipo } : {}),
    ...(/^\d+$/.test(f.folio) ? { folio: f.folio } : {}),
    ...(f.cliente.trim().length >= 2 ? { cliente: f.cliente.trim() } : {}),
    ...(f.desde ? { desde: f.desde } : {}),
    ...(f.hasta ? { hasta: f.hasta } : {}),
    pagina: f.pagina,
  };
  const q = useQuery({
    queryKey: ['historico-random', consulta] as const,
    queryFn: () =>
      api.get<{ items: DocumentoRandomListado[] }>('/historico/documentos', { query: consulta }),
    staleTime: 60_000,
  });
  const filas = q.data?.items ?? [];

  const columnas: Columna<DocumentoRandomListado>[] = [
    { id: 'emp', titulo: 'Emp.', ancho: '3.5rem', celda: (d) => d.empresa },
    {
      id: 'tipo',
      titulo: 'Documento',
      ancho: '10rem',
      celda: (d) => TIPOS_RANDOM[d.tido] ?? d.tido,
    },
    {
      id: 'folio',
      titulo: 'Folio',
      ancho: '7rem',
      alinear: 'der',
      celda: (d) => <span className="mono">{folioCorto(d.folio)}</span>,
    },
    { id: 'fecha', titulo: 'Fecha', ancho: '7rem', celda: (d) => fecha(d.fecha) },
    {
      id: 'cliente',
      titulo: 'Cliente',
      celda: (d) => (
        <>
          {d.cliente ?? '—'}
          {d.rut && <span className="apagado mono"> · {rut(d.rut.replace(/^0+/, ''))}</span>}
        </>
      ),
    },
    { id: 'total', titulo: 'Total', alinear: 'der', ancho: '8rem', celda: (d) => pesos(d.total) },
    { id: 'estado', titulo: 'Estado', ancho: '9rem', celda: (d) => estadoRandom(d) },
  ];

  return (
    <div className="pantalla consulta" data-pantalla="documentos-random">
      <p className="apagado">
        Documentos emitidos en Random, para consultar. Escribe el folio (o el RUT o nombre del
        cliente) y abre el documento con Enter. Copia del 30-09-2026: lo emitido después en Random
        todavía no está aquí.
      </p>
      <div className="acciones-fila">
        <label className="campo">
          Empresa
          <select
            aria-label="Empresa en Random"
            value={f.empresa}
            onChange={(e) => cambiar({ empresa: e.target.value })}
          >
            {Object.entries(EMPRESAS_RANDOM).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
            <option value="">Todas</option>
          </select>
        </label>
        <label className="campo">
          Documento
          <select
            aria-label="Tipo de documento"
            value={f.tipo}
            onChange={(e) => cambiar({ tipo: e.target.value })}
          >
            {Object.entries(TIPOS_RANDOM).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
            <option value="">Todos</option>
          </select>
        </label>
        <label className="campo">
          Folio
          <input
            aria-label="Folio"
            inputMode="numeric"
            data-autofoco
            value={f.folio}
            onChange={(e) => cambiar({ folio: e.target.value.replace(/\D/g, '') })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filas.length === 1) setAbierto(filas[0]!.id);
            }}
          />
        </label>
        <label className="campo">
          Cliente
          <input
            aria-label="Cliente (RUT o nombre)"
            placeholder="RUT o nombre"
            value={f.cliente}
            onChange={(e) => cambiar({ cliente: e.target.value })}
          />
        </label>
        <label className="campo">
          Desde
          <input
            type="date"
            aria-label="Desde"
            value={f.desde}
            onChange={(e) => cambiar({ desde: e.target.value })}
          />
        </label>
        <label className="campo">
          Hasta
          <input
            type="date"
            aria-label="Hasta"
            value={f.hasta}
            onChange={(e) => cambiar({ hasta: e.target.value })}
          />
        </label>
      </div>
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Documentos de Random"
        columnas={columnas}
        filas={filas}
        clave={(d) => d.id}
        cargando={q.isLoading}
        vacio="Sin documentos con esos datos"
        alAbrir={(d) => setAbierto(d.id)}
        paginacion={{
          pagina: f.pagina,
          hayMas: filas.length >= 50,
          alCambiar: (pagina) => setF((x) => ({ ...x, pagina })),
        }}
      />
      {abierto && <VerDocumentoRandom id={abierto} alCerrar={() => setAbierto(null)} />}
    </div>
  );
}

/** El documento como se emitió: emisor, cliente, detalle y totales. Se puede imprimir. */
export function VerDocumentoRandom({ id, alCerrar }: { id: string; alCerrar: () => void }) {
  const q = useQuery({
    queryKey: ['historico-random', 'doc', id] as const,
    queryFn: () => api.get<DocumentoRandom>(`/historico/documentos/${id}`),
    staleTime: Infinity,
  });
  const [imprimiendo, setImprimiendo] = useState(false);
  const d = q.data;
  const titulo = d ? `${TIPOS_RANDOM[d.tido] ?? d.tido} N° ${folioCorto(d.folio)}` : 'Documento';
  return (
    <Modal
      titulo={titulo}
      ancho="ancho"
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Cerrar
          </button>
          <button
            type="button"
            className="primario"
            disabled={!d}
            onClick={() => setImprimiendo(true)}
          >
            Imprimir
          </button>
        </>
      }
    >
      {q.isError && <PanelError error={q.error} />}
      {!d && !q.isError && <p className="apagado">Cargando…</p>}
      {d && <CuerpoDocumentoRandom d={d} />}
      {d && imprimiendo && (
        <Imprimir alTerminar={() => setImprimiendo(false)}>
          <EncabezadoImpreso
            empresa={d.emisor.razon}
            titulo={`${TIPOS_RANDOM[d.tido] ?? d.tido} (emitida en Random)`}
            numero={`N° ${folioCorto(d.folio)}`}
          />
          <CuerpoDocumentoRandom d={d} />
        </Imprimir>
      )}
    </Modal>
  );
}

function CuerpoDocumentoRandom({ d }: { d: DocumentoRandom }) {
  const conDescuento = d.lineas.some((l) => Number(l.descuento ?? 0) !== 0);
  return (
    <div className="documento-random" data-documento-random={d.id}>
      <section className="ficha-dos-columnas">
        <div>
          <strong>{d.emisor.razon}</strong>
          {d.emisor.rut && <div className="mono">RUT {d.emisor.rut}</div>}
          {d.emisor.giro && <div className="apagado">{d.emisor.giro}</div>}
          {(d.emisor.direccion || d.emisor.ciudad) && (
            <div className="apagado">
              {[d.emisor.direccion, d.emisor.ciudad].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
        <div>
          <div>
            <strong>{TIPOS_RANDOM[d.tido] ?? d.tido}</strong> N° {folioCorto(d.folio)}
            {d.electronico ? ' · electrónica' : ''}
          </div>
          <div>
            Emisión {fecha(d.fecha)}
            {d.vencimiento && ` · vence ${fecha(d.vencimiento)}`}
          </div>
          <div>
            Estado: <strong data-estado-random>{estadoRandom(d)}</strong>
          </div>
          {d.sucursal && <div className="apagado">Sucursal {d.sucursal}</div>}
          {d.funcionario && <div className="apagado">Vendedor / funcionario {d.funcionario}</div>}
        </div>
      </section>
      <section>
        <h4>Cliente</h4>
        <div>
          {d.cliente ?? '—'}
          {d.rut && <span className="mono"> · RUT {rut(d.rut.replace(/^0+/, ''))}</span>}
          {d.sucursalCliente && <span className="apagado"> · suc. {d.sucursalCliente}</span>}
        </div>
        {d.direccionDespacho && <div className="apagado">Despacho: {d.direccionDespacho}</div>}
        {d.ordenCompra && <div className="apagado">Orden de compra: {d.ordenCompra}</div>}
      </section>
      <table className="grilla compacta" aria-label="Detalle del documento">
        <thead>
          <tr>
            <th>#</th>
            <th>Código</th>
            <th>Descripción</th>
            <th className="al-der">Cantidad</th>
            <th className="al-der">Precio</th>
            {conDescuento && <th className="al-der">Desc.</th>}
            <th className="al-der">Total</th>
            <th>Origen</th>
          </tr>
        </thead>
        <tbody>
          {d.lineas.map((l) => (
            <tr key={l.linea}>
              <td>{l.linea}</td>
              <td className="mono">{l.sku}</td>
              <td>{l.descripcion}</td>
              <td className="al-der">
                {l.cantidad !== null ? decimal(l.cantidad) : ''} {l.unidad}
              </td>
              <td className="al-der">{l.precioBruto !== null ? pesos(l.precioBruto) : ''}</td>
              {conDescuento && <td className="al-der">{l.descuento ? pesos(l.descuento) : ''}</td>}
              <td className="al-der">{l.bruto !== null ? pesos(l.bruto) : ''}</td>
              <td className="apagado">
                {l.origenTido
                  ? `${TIPOS_RANDOM[l.origenTido] ?? l.origenTido} ${folioCorto(l.origenFolio)}`
                  : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <section className="totales-documento" aria-label="Totales del documento">
        <div>
          Neto <strong>{pesos(d.neto)}</strong>
        </div>
        <div>
          IVA <strong>{pesos(d.iva)}</strong>
        </div>
        {Number(d.impuestos) !== 0 && (
          <div>
            Impuestos adicionales <strong>{pesos(d.impuestos)}</strong>
          </div>
        )}
        <div>
          Total <strong>{pesos(d.total)}</strong>
        </div>
        {Number(d.abonado) !== 0 && <div className="apagado">Abonado {pesos(d.abonado)}</div>}
      </section>
      {d.observacion && <p className="apagado">Observación: {d.observacion}</p>}
    </div>
  );
}
